/**
 * Crossfades between its `[data-slide]` children on a timer (used for two-photo social tiles).
 *
 * Usage:
 *   <image-crossfade interval="4200">
 *     <img data-slide data-active …>
 *     <img data-slide …>
 *     <span data-dots><i data-active></i><i></i></span>
 *   </image-crossfade>
 *
 * Runs only while visible and never for users who prefer reduced motion.
 */
class ImageCrossfade extends HTMLElement {
  /** @type {number | null} */
  #timer = null;
  /** @type {IntersectionObserver | null} */
  #observer = null;
  #index = 0;

  connectedCallback() {
    if (this.slides.length < 2 || window.matchMedia('(prefers-reduced-motion: reduce)').matches) return;

    this.#observer = new IntersectionObserver(
      ([entry]) => {
        if (entry?.isIntersecting) this.#start();
        else this.#stop();
      },
      { threshold: 0.1 }
    );
    this.#observer.observe(this);
  }

  disconnectedCallback() {
    this.#observer?.disconnect();
    this.#stop();
  }

  get slides() {
    return Array.from(this.querySelectorAll(':scope > [data-slide]'));
  }

  get dots() {
    return Array.from(this.querySelectorAll(':scope > [data-dots] > *'));
  }

  #start() {
    if (this.#timer !== null) return;
    const interval = Number(this.getAttribute('interval')) || 4200;
    this.#timer = window.setInterval(() => this.#next(), interval);
  }

  #stop() {
    if (this.#timer === null) return;
    window.clearInterval(this.#timer);
    this.#timer = null;
  }

  #next() {
    const slides = this.slides;
    this.#index = (this.#index + 1) % slides.length;

    slides.forEach((slide, index) => slide.toggleAttribute('data-active', index === this.#index));
    this.dots.forEach((dot, index) => dot.toggleAttribute('data-active', index === this.#index));
  }
}

if (!customElements.get('image-crossfade')) {
  customElements.define('image-crossfade', ImageCrossfade);
}
